// <emma-chat-message-list> — outer container for the conversation.
//
// Renders state.turnHistory as a vertical column of <emma-chat-turn>
// instances. Marks the last entry as `last` (refine input renders there);
// earlier entries as `previous` (collapsed plate). Optionally inserts a
// transient scanning-placeholder bubble between the user-just-asked and
// the (eventual) emma response, driven by the double-trigger described in
// audit §6.3:
//
//   1. setTimeout(400ms) fires → scanningMinElapsed = true
//   2. emma-chat-thinking-start CustomEvent (from T12 root SSE handler)
//      OR setTimeout(2000ms) safety fallback → scanningSignalReceived = true
//   3. When both true → render placeholder
//
// Reset on isSubmitting → false (parent flips it after finalizeTurn).
//
// Cross-component events bubble up through Shadow DOM via composed: true,
// so emma-card-click / emma-refine-submit / emma-deploy-click etc. reach
// the root (T12) without manual re-emit at this layer.

import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import {
  chatStateContext,
  StateConsumerController,
  type ChatStateStore,
  type TurnEntry,
} from '../state/index.js';
import type { ResultCard } from '../types/search.js';
import { designTokens } from './design-tokens.js';
import { dispatchStopThinking, stopIcon } from './stop-thinking.js';
import './emma-chat-turn.js';
import './emma-chat-avatar.js';

// Portal bottom-CTA reveal delay. A result turn runs the full reveal timeline
// in <emma-chat-turn> (scanning → cards → refine-zone settles ~4480ms). We mount
// the CTA only after this delay (not before — so no empty layout gap is reserved
// while waiting), then fade it in as the final step, just after the refine block.
const CTA_REVEAL_DELAY_MS = 4500;

@customElement('emma-chat-message-list')
export class EmmaChatMessageList extends LitElement {
  // `subscribe: true` is critical: without it, @consume fires context-request
  // ONCE at connectedCallback. If the ContextProvider attaches later (common
  // in playgrounds, dynamic mounts, or Vue host integrations), the consumer
  // misses the response and `store` stays undefined forever. With subscribe,
  // the consumer keeps listening for provider attachment AND for value changes.
  @consume({ context: chatStateContext, subscribe: true })
  @property({ attribute: false })
  store?: ChatStateStore;

  /** Live thinking-progress message during refine search. Pushed down
   *  from root → turn → refine, surfaces in the .refine-scanning
   *  indicator below the refine row when submitted=true. */
  @property({ type: String, attribute: 'thinking-message' })
  thinkingMessage = '';

  /** Soft refine cap — propagated to the turn so it can hide the refine
   *  UI when turnHistory exceeds the threshold and show a "ready to
   *  deploy" hint instead. Configured at root level. */
  @property({ type: Number, attribute: 'max-refines' })
  maxRefines = 5;

  /** BFF URL prefix passed down to leaf components so they can resolve
   *  asset paths (avatar, country flags, spot icon, provider logos)
   *  against the BFF origin instead of the host page's. Empty in
   *  same-origin landing context. */
  @property({ type: String, attribute: 'api-base' })
  apiBase = '';

  /** Embed surface, threaded from <emma-chat> down to <emma-chat-turn> so RAG
   *  source citations can dispatch the in-app Docsie event on "portal". */
  @property({ type: String })
  surface = '';

  /** True when embedded in the portal (the host owns deploy via
   *  `emma-deploy-click`). Drives the bottom-CTA copy: inside the portal the
   *  user is already authed and the action is a direct deploy → "Deploy";
   *  standalone (landing) it's the signup gate → "Continue with emma". */
  @property({ type: Boolean, attribute: 'external-deploy-handler' })
  externalDeployHandler = false;

  /** Carry-deploy only: gates RENDERING of the bottom CTA so it isn't in the
   *  DOM (reserving layout space) during the reveal wait. Flipped by a timer
   *  once the carry-deploy result is on screen; reset when a new request loads. */
  @state() private ctaRevealed = false;
  private ctaRevealTimer: number | null = null;

  constructor() {
    super();
    // StateConsumerController registers itself with this host on
    // construction (host.addController). We don't retain the reference —
    // the host's `controllers` array keeps it alive.
    new StateConsumerController(this, () => this.store);
  }

  override updated(): void {
    this.syncCtaReveal();
  }

  override disconnectedCallback(): void {
    this.clearCtaTimer();
    super.disconnectedCallback();
  }

  /** Drive `ctaRevealed` for the carry-deploy bottom CTA: hide+reset while a
   *  request is loading / before the config result lands, then schedule a
   *  one-shot reveal timed just after the turn's refine block. Rendering (not
   *  just opacity) is gated on the flag, so no empty layout gap is reserved
   *  during the wait. */
  private syncCtaReveal(): void {
    // Applies to every PORTAL turn (carry-deploy, refines, fresh-portal search)
    // — each runs the full reveal timeline, so the CTA must follow the refine
    // block every time, not just on the initial carry-deploy turn.
    const submitting = this.store?.state.isSubmitting ?? false;
    const turns = this.store?.state.turnHistory ?? [];
    const last = turns[turns.length - 1];
    const hasResult = !!last?.sseData.result?.results?.length;

    if (submitting || !this.externalDeployHandler || !hasResult) {
      if (this.ctaRevealed) this.ctaRevealed = false;
      this.clearCtaTimer();
      return;
    }
    // Result is on screen and settled — schedule the reveal ONCE. The
    // `ctaRevealTimer !== null` guard makes this idempotent across the many
    // updated() calls that fire while waiting (no second timer is ever armed);
    // a pending timer is torn down above only when the turn changes (submit).
    if (this.ctaRevealed || this.ctaRevealTimer !== null) return;
    // A hydrated (dialog-carry) last turn renders instantly with no reveal
    // theatre — its CTA must appear immediately too, or the bottom CTA sits
    // hidden for the full reveal delay over already-settled content.
    if (last?.hydrated || this.reducedMotion()) {
      this.ctaRevealed = true;
      return;
    }
    this.ctaRevealTimer = window.setTimeout(() => {
      this.ctaRevealTimer = null;
      this.ctaRevealed = true;
    }, CTA_REVEAL_DELAY_MS);
  }

  private clearCtaTimer(): void {
    if (this.ctaRevealTimer !== null) {
      clearTimeout(this.ctaRevealTimer);
      this.ctaRevealTimer = null;
    }
  }

  private reducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  static override styles = [
    designTokens,
    css`
      :host {
        display: block;
        font-family: var(--font-sans);
      }
      .message-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      /* Synthetic loader bubble shown after the last turn while a
         submit is in flight AND the last turn doesn't render its own
         indicator (OOB / deploy / pick / consultative / soft-gated).
         Lifts .chat-row + .chat-bubble--emma styling from <emma-chat-turn>
         (DS chat-bubble.css) — kept compact (smaller padding) since
         it's just an acknowledge bubble, not a content bubble. */
      .synthetic-loader-row {
        display: flex;
        gap: 8px;
        align-items: flex-start;
        animation: synthetic-loader-arrive 200ms ease-out both;
      }
      @keyframes synthetic-loader-arrive {
        0% {
          opacity: 0;
          transform: translateY(4px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .synthetic-loader-row {
          animation: none;
        }
      }
      .synthetic-loader-bubble {
        flex: 1;
        min-width: 0;
        background: var(--gray-03);
        border-radius: var(--radius-md);
        border-top-left-radius: 2px;
        padding: 12px 16px;
        font-size: var(--text-sm);
        color: var(--color-text);
      }
      .synthetic-loader {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .synthetic-loader-label {
        font-size: var(--text-sm);
        color: var(--gray-60);
      }
      /* Stop control at the right edge of the loader bubble — cancels the
         in-flight search (bubbles emma-stop-thinking to root <emma-chat>). */
      .thinking-stop {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-left: auto;
        padding: 4px 10px;
        background: none;
        border: 1px solid var(--gray-10);
        border-radius: var(--radius-sm);
        color: var(--color-text-muted);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        line-height: 1;
        cursor: pointer;
        transition:
          color var(--duration-fast) var(--easing),
          background var(--duration-fast) var(--easing);
      }
      .thinking-stop svg {
        flex: none;
      }
      @media (hover: hover) {
        .thinking-stop:hover {
          color: var(--color-text);
          background: var(--gray-10);
        }
      }
      /* Reuse the same typing-dots keyframes used by emma-chat-turn —
         3 small blue dots bouncing in sequence. Defined here locally
         since this Shadow DOM doesn't inherit emma-chat-turn's styles. */
      .typing-dots {
        display: flex;
        gap: 4px;
      }
      .typing-dots span {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: var(--blue-60);
        animation: typing-dot-bounce 1.2s var(--easing) infinite;
      }
      .typing-dots span:nth-child(2) {
        animation-delay: 0.15s;
      }
      .typing-dots span:nth-child(3) {
        animation-delay: 0.3s;
      }
      @keyframes typing-dot-bounce {
        0%,
        80%,
        100% {
          transform: scale(0.7);
          opacity: 0.4;
        }
        40% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .typing-dots span {
          animation: none;
          opacity: 0.7;
        }
      }

      /* Bottom CTA (DS button-cta.css + search-results.html state 1/3).
         Single button under all turns — works for any commit action
         (deploy / pick / generic result). The .message-list flex gap
         already separates the .cta-zone from the last turn (DS
         .chat-frame uses one gap value for all children); no extra
         margin here. .cta-terminal wraps the button with descriptive
         text above per DS state 3 pattern (reserved, unused).

         Delayed reveal: CSS animation with 3220ms delay matches monolith
         showResponse :3521 — ctaBlock.is-visible fires 3220ms after
         submit on initial turn. The animation runs ONCE on mount; Lit
         reuses the .cta-zone DOM node across re-renders, so subsequent
         turn updates don't restart the animation — CTA stays visible.

         Portal mode (.cta-zone--carry, externalDeployHandler): every portal
         result turn runs the full reveal timeline (emma-chat-turn — scanning →
         cards → refine-zone settles ~4480ms). The CTA must arrive AFTER the
         refine block. Rather than a CSS delay (which reserves an empty layout
         gap while waiting), syncCtaReveal MOUNTS the .cta-zone only at reveal
         time (CTA_REVEAL_DELAY_MS) — so here it's just a fade-in on mount. */
      .cta-zone {
        animation: cta-zone-arrive var(--duration-normal, 200ms) var(--easing) 3220ms both;
      }
      .cta-zone--carry {
        animation: cta-zone-arrive var(--duration-normal, 200ms) var(--easing) both;
      }
      @keyframes cta-zone-arrive {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .cta-zone,
        .cta-zone--carry {
          animation: none;
        }
      }
      .cta-terminal {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      .cta-terminal-text {
        text-align: center;
        font-size: var(--text-sm);
        color: var(--color-text-muted);
        line-height: var(--leading-md);
      }
      .cta-terminal-text strong {
        display: block;
        color: var(--color-text);
        font-weight: var(--font-medium);
      }
      .btn-cta {
        display: block;
        width: 100%;
        padding: 14px 32px;
        background: var(--blue-accent);
        color: var(--white);
        font-family: var(--font-sans);
        font-size: var(--text-md);
        font-weight: var(--font-semibold);
        text-align: center;
        border: none;
        border-radius: var(--radius-md);
        cursor: pointer;
        transition:
          background var(--duration-fast) var(--easing),
          transform var(--duration-fast) var(--easing);
      }
      @media (hover: hover) {
        .btn-cta:hover {
          background: var(--blue-80);
        }
      }
      .btn-cta:active {
        transform: scale(0.95);
      }
      @media (prefers-reduced-motion: reduce) {
        .btn-cta,
        .btn-cta:active {
          transition: none;
          transform: none;
        }
      }
      /* Unobtrusive "Start over" under the bottom CTA (portal launcher only).
         Muted text-button, full width, sits directly below the blue CTA. */
      .cta-restart {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        width: 100%;
        margin-top: var(--space-2);
        padding: 6px 12px;
        background: none;
        border: none;
        border-radius: var(--radius-sm);
        color: var(--color-text-muted);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition:
          color var(--duration-fast) var(--easing),
          background var(--duration-fast) var(--easing);
      }
      .cta-restart svg {
        flex: none;
        opacity: 0.85;
      }
      @media (hover: hover) {
        .cta-restart:hover {
          color: var(--color-text);
          background: var(--gray-10);
        }
      }
    `,
  ];

  override render() {
    const turns = this.store?.state.turnHistory ?? [];
    const lastIndex = turns.length - 1;
    const lastTurn = turns[lastIndex];

    // Synthetic in-flight loader is rendered ONLY when no other
    // submit-feedback channel is visible:
    //   - Active result-turn carries its own refine-input with a
    //     .refine-scanning indicator → no synthetic loader needed.
    //   - OOB / deploy / pick / consultative turn has no input — when
    //     a chip from those bubbles fires a refine, we'd have NO visual
    //     feedback until the next turn lands (looks frozen). For those
    //     cases we render a small typing-dots emma bubble below the
    //     last turn so the user sees something acknowledge the click.
    const submitting = this.store?.state.isSubmitting ?? false;
    const selectedCardId = this.store?.state.selectedCardId ?? null;
    // Turn index that owns the selected card (`tXcY` → X). Used to keep a
    // dialog-carry (hydrated) previous turn holding the user's pick open,
    // instead of collapsed to a one-line plate that hides the highlight.
    const selectedTurnIndex = selectedCardId
      ? Number(selectedCardId.match(/^t(\d+)c\d+$/)?.[1] ?? -1)
      : -1;
    // Past the soft cap, hide the refine UI on the active turn so the
    // bottom CTA becomes the primary commit affordance. Mirrors monolith
    // :3559-3577 maybeShowSignupGate logic — turnHistory length exceeds
    // maxRefines → softGate=true. Active turn checks this prop to skip
    // its refine-zone render.
    // No refine cap in-portal: the soft gate is the STANDALONE signup funnel
    // (anonymous hero → push to sign up after N refines). An authenticated user
    // already inside the portal can refine without limit.
    const softGate = this.surface !== 'portal' && turns.length > this.maxRefines;
    const lastHasResult =
      !!lastTurn?.sseData.result?.results?.length;
    // A consultative (doc) turn renders its OWN follow-up refine-input (with the
    // .refine-scanning "Composing…" indicator) on the last turn — so a synthetic
    // loader below it is a DOUBLE indicator. Treat it as having its own.
    const lastIsConsultativeOnly =
      !!lastTurn?.sseData.consultative && !lastTurn?.sseData.result;
    // An optimistic pending turn (chip echo) already shows its own thinking
    // bubble (typing dots + message) — the synthetic loader would double it.
    const lastIsPending = !!lastTurn?.pending;
    // A host-injected system-event turn (deploy bubble) renders its OWN follow-up
    // refine-input (with the .refine-scanning indicator) on the last turn — same
    // as consultative — so a synthetic loader below it would double the indicator.
    const lastIsSystemEvent = !!lastTurn?.systemEvent;
    // A doc-card turn renders its OWN follow-up refine-input on the last turn
    // (same as system-event / consultative) — a synthetic loader under it would
    // be a second in-flight indicator.
    const lastIsDocCard = !!lastTurn?.docCard;
    // OOB / deploy_intent / pick_result terminals each render their OWN inline
    // follow-up refine-input (batch4 #16). When one of those is the last turn and
    // a typed follow-up runs WITHOUT an optimistic pending turn, the turn stays
    // `last` while submitting → its .refine-scanning row shows. A synthetic loader
    // below it would then be a DOUBLE indicator (two "Searching…" + two Stop).
    const lastIsNonResultTerminal =
      !!lastTurn?.sseData.oob ||
      !!lastTurn?.sseData.deploy_intent ||
      !!lastTurn?.sseData.pick_result;
    // Synthetic loader only when the last turn has NO in-bubble indicator
    // (soft-gated). Result-refine, consultative-followup, system-event, pending,
    // and the OOB/deploy/pick inline refines all carry their own → suppress to
    // avoid a double indicator.
    const lastHasOwnIndicator =
      (lastHasResult && !softGate) ||
      lastIsConsultativeOnly ||
      lastIsPending ||
      lastIsSystemEvent ||
      lastIsDocCard ||
      lastIsNonResultTerminal;
    const showSyntheticLoader = submitting && lastTurn != null && !lastHasOwnIndicator;
    return html`
      <div class="message-list">
        ${turns.map(
          (entry, i) => html`
            <emma-chat-turn
              .entry=${entry}
              .index=${i}
              .pickedCards=${this.resolvePickedCards(entry, turns)}
              ?submitting=${submitting}
              thinking-message=${this.thinkingMessage}
              ?soft-gate=${softGate}
              ?last=${i === lastIndex}
              ?previous=${i < lastIndex}
              ?force-expanded=${entry.hydrated === true && i === selectedTurnIndex && i < lastIndex}
              .selectedCardId=${selectedCardId}
              api-base=${this.apiBase}
              surface=${this.surface}
            ></emma-chat-turn>
          `,
        )}
        ${showSyntheticLoader ? this.renderSyntheticLoader() : nothing}
        ${lastTurn ? this.renderBottomCta(lastTurn, turns) : nothing}
      </div>
    `;
  }

  /** Stop button inside the synthetic loader — cancel the in-flight
   *  search. Bubbles emma-stop-thinking to root <emma-chat>, which
   *  aborts the SSE and clears isSubmitting (the loader unmounts). */
  private handleStopClick = (): void => {
    dispatchStopThinking(this);
  };

  /** Small "thinking" bubble shown after the last turn during a submit
   *  when no in-bubble refine indicator exists (OOB / deploy / pick /
   *  consultative / soft-gated last turn). Mirrors the typing-dots
   *  bubble that introduces the active result-turn timeline so the
   *  visual language is consistent. The label reuses the canonical
   *  thinkingMessage cycled by root <emma-chat>'s startThinkingCycle
   *  (scenario-aware: "Searching providers…" / "Scanning GPU
   *  availability…" / "Checking K8s configurations…" etc.) — no
   *  hardcoded fallback string. Auto-removed when the next turn is
   *  pushed (isSubmitting flips to false). */
  private renderSyntheticLoader() {
    return html`
      <div class="chat-row chat-row--emma synthetic-loader-row">
        <emma-chat-avatar></emma-chat-avatar>
        <div class="chat-bubble--emma synthetic-loader-bubble">
          <div class="synthetic-loader">
            <div class="typing-dots" aria-hidden="true">
              <span></span><span></span><span></span>
            </div>
            ${this.thinkingMessage
              ? html`<span class="synthetic-loader-label">${this.thinkingMessage}</span>`
              : nothing}
            <button
              type="button"
              class="thinking-stop"
              aria-label="Stop search"
              @click=${this.handleStopClick}
            >
              ${stopIcon()} Stop
            </button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Bottom CTA per DS rule (search-results.html state 1/3): one
   * "Continue with emma" button under the conversation, regardless of
   * which actionable state the last turn is in (result / deploy / pick).
   * Hidden for OOB-only and consultative-only conversations — those have
   * no config to deploy.
   *
   * Terminal states wrap the button in .cta-terminal with descriptive
   * text above (state 3 pattern). For deploy_intent and pick_result the
   * descriptive text reuses the bubble's own narrative so we DON'T
   * duplicate text; just show the bare button under those bubbles.
   * Reserve .cta-terminal for the soft-gate / "best deal" terminal hint
   * — a future T11/T12 refinement.
   */
  private renderBottomCta(last: TurnEntry, turns: TurnEntry[]) {
    const data = last.sseData;
    const submitting = this.store?.state.isSubmitting ?? false;
    const hasResult =
      !!data.result && !!data.result.results && data.result.results.length > 0;
    if (this.externalDeployHandler) {
      // Portal embed: "Deploy to project" appears ONLY once a result card has
      // loaded — not on a bare deploy_intent, and not while a search / config
      // fetch is in flight (a prior turn's result would otherwise leave a stale
      // CTA on screen during load).
      if (!hasResult || submitting) return nothing;
      // Hold RENDERING (not just opacity) until the timed reveal (syncCtaReveal)
      // so no empty layout gap is reserved while the turn's refine block lands —
      // the CTA mounts + fades in as the final step.
      if (!this.ctaRevealed) return nothing;
    } else {
      // Standalone (landing): the signup-gate CTA shows for any actionable turn.
      const isActionable = hasResult || !!data.deploy_intent || !!data.pick_result;
      if (!isActionable) return nothing;
    }

    // Manual selection wins: when the user clicked any card across the
    // conversation, that card's ctaUrl drives the bottom CTA.
    //
    // No manual click → fall back to the auto-highlighted best-price
    // card from the LAST turn (same card emma-chat-turn's computeSelected
    // visually highlights via .selected). This matches user intent: the
    // card the UI marks as "best deal" is what Continue with emma should
    // deploy when the user hasn't picked anything else explicitly.
    //
    // Final fallback: LLM's deploy_intent.ctaUrl (e.g. for pick/deploy
    // turns with no card array), or '' which root T12 treats as
    // "POST /api/hero/action with current resolvedParams".
    const selectedCardId = this.store?.state.selectedCardId ?? null;
    let selectedCard: ResultCard | null = null;
    if (selectedCardId) {
      selectedCard = this.findCardById(selectedCardId, turns);
    } else if (hasResult) {
      const lastCards = data.result!.results;
      const bestIdx = lastCards.findIndex((c) => c.isBestPrice);
      if (bestIdx >= 0) selectedCard = lastCards[bestIdx]!;
    }
    const ctaUrl = selectedCard?.ctaUrl || data.deploy_intent?.ctaUrl || '';
    // Context-aware CTA copy. Embedded in the portal (externalDeployHandler):
    // the user is already authed and the click carry-deploys into the wizard →
    // "Deploy". Standalone (landing): the click is the signup gate → the BFF's
    // ctaText ("Continue with emma"). The BFF is embedding-agnostic, so the
    // standalone→portal relabel is decided here on the client.
    const deployLabel = data.deploy_intent?.ctaText;
    const label = this.externalDeployHandler
      ? 'Deploy to project'
      : deployLabel || 'Continue with emma';

    // Soft-gate hint per monolith :2284-2288 + :3833 showSignupGate.
    // Once turnCount > maxRefines, refine UI is hidden (turn-level) and
    // the CTA gets a descriptive .cta-terminal lead — "this is your best
    // deal, time to commit".
    // No refine cap in-portal: the soft gate is the STANDALONE signup funnel
    // (anonymous hero → push to sign up after N refines). An authenticated user
    // already inside the portal can refine without limit.
    const softGate = this.surface !== 'portal' && turns.length > this.maxRefines;

    const button = html`
      <button
        class="btn-cta"
        type="button"
        @click=${() => this.handleBottomCtaClick(ctaUrl, selectedCard)}
      >
        ${label}
      </button>
    `;

    // Persistent "Start over" affordance under the bottom CTA. Reuses the SAME
    // emma-clear-conversation event as the OOB chip — it bubbles to root
    // <emma-chat>, which plays the 200ms fade-out and store.reset().
    //
    //  - Portal launcher embed (externalDeployHandler): the launcher keeps the
    //    widget session alive across open/close, so a prior pick + refine
    //    lingers (even after a deploy); this lets the user clear it.
    //  - Standalone (QA #4): a persistent Start over on RESULT turns, where the
    //    bubble's refine-zone carries no inline Start over of its own — without
    //    it a standalone user with an ordinary result thread could only reset by
    //    reloading. Non-result terminals (OOB / deploy / pick / consultative)
    //    already render their own inline Start over (emma-chat-turn), so this is
    //    gated to hasResult to avoid a doubled control. The EMPTY standalone
    //    landing keeps its clean single-shot entry (no lastTurn → not rendered).
    const showRestart =
      this.externalDeployHandler || (this.surface !== 'portal' && hasResult);
    const restart = showRestart
      ? html`
          <button
            class="cta-restart"
            type="button"
            @click=${this.handleRestart}
            aria-label="Start a new conversation"
          >
            <svg aria-hidden="true" width="11" height="11" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 4v3.5h3.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M3.2 7.5C4 5 6 3.5 8.5 3.5c3 0 5.5 2.5 5.5 5.5s-2.5 5.5-5.5 5.5c-2.5 0-4.5-1.5-5.2-3.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Start over
          </button>
        `
      : nothing;

    return html`
      <div class="cta-zone${this.externalDeployHandler ? ' cta-zone--carry' : ''}">
        ${softGate
          ? html`
              <div class="cta-terminal">
                <div class="cta-terminal-text">
                  <strong>That's your best deal.</strong>
                  <span>Your config is ready to deploy.</span>
                </div>
                ${button}
              </div>
            `
          : button}
        ${restart}
      </div>
    `;
  }

  /** Persistent "Start over" under the bottom CTA (portal launcher only).
   *  Bubbles emma-clear-conversation to root <emma-chat>, which plays the
   *  200ms fade-out + store.reset() — the same reset path as the OOB chip. */
  private handleRestart = (): void => {
    this.dispatchEvent(
      new CustomEvent('emma-clear-conversation', { bubbles: true, composed: true }),
    );
  };

  /** Bottom-CTA dispatch. detail.url preserved for backward-compat with
   *  the landing flow; structured fields added so the portal HeroDeployPage
   *  gateway can look up the portal-enriched config via /v4/wizard-configurations
   *  without re-fetching from BFF. All structured fields come from the
   *  selectedCard (manual selection or best-price fallback). The `url`
   *  field stays populated for the landing path that follows
   *  externalDeployHandler === false.
   *
   *  Note on K8s (Phase 3a): K8s result-cards now dispatch the same
   *  structured emma-deploy-click as vm/spot (workload:'kubernetes' +
   *  criteria). The portal gateway routes kubernetes deploy-clicks to the
   *  managedK8sWizard and seeds the shared VM wizard via the workerNodes
   *  path (managedK8sSchema workerNodes is type:"new_vm_group"). Before
   *  Phase 3a this dispatched emma-navigate-intent (navigate-only, no
   *  criteria); the carry-navigate prompt in emma-chat-turn keeps that
   *  event for back-compat with unmigrated portal builds. */
  private handleBottomCtaClick = (url: string | undefined, card: ResultCard | null): void => {
    const detail: Record<string, unknown> = { url: url ?? '' };
    if (card) {
      detail.configId = card.configId;
      detail.workload = card.workloadType;
      detail.providerName = card.provider;
      if (card.providerId !== undefined) detail.providerId = card.providerId;
      if (card.locationId !== undefined) detail.locationId = card.locationId;
      if (card.dataCenterId) detail.dataCenterId = card.dataCenterId;
      if (card.ramGb !== undefined) detail.ramGb = card.ramGb;
      if (card.vcpu !== undefined) detail.vCpu = card.vcpu;
      if (card.volumeGb !== undefined) detail.volumeSizeGb = card.volumeGb;
      if (card.volumeType) detail.volumeType = card.volumeType;
      if (card.acceleratorId) detail.acceleratorId = card.acceleratorId;
      if (card.gpuNumber !== undefined) detail.gpuNumber = card.gpuNumber;
      if (card.cloudNetworkTypes) detail.cloudNetworkTypes = card.cloudNetworkTypes;
      if (card.vcpuType) detail.vcpuType = card.vcpuType;
      // Phase 3b (K8s ASG): the autoscale bracket. Its presence tells the
      // portal gateway to seed an autoscaling_configuration group (NOT a
      // single workerNode) and to skip showWizard. Only K8s cards carry it.
      if (card.nodeMin !== undefined) detail.nodeMinCount = card.nodeMin;
      if (card.nodeMax !== undefined) detail.nodeMaxCount = card.nodeMax;
      // Phase 3b: K8s deploy mode (worker|asg|both) — the portal seeds the
      // matching form (worker node / autoscaling group / both).
      if (card.k8sMode) detail.k8sMode = card.k8sMode;
      // Phase 3c: static worker-node count → portal worker node Quantity.
      if (card.k8sWorkerCount !== undefined) detail.workerCount = card.k8sWorkerCount;
    }
    this.dispatchEvent(
      new CustomEvent('emma-deploy-click', {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  };

  /** Resolve a single `tXcY` reference to its ResultCard. Used by the
   *  bottom CTA to pull the user-selected card's ctaUrl. Returns null
   *  when the reference doesn't match any turn / card. */
  private findCardById(id: string, turns: TurnEntry[]): ResultCard | null {
    const m = id.match(/^t(\d+)c(\d+)$/);
    if (!m) return null;
    const ti = parseInt(m[1]!, 10);
    const ci = parseInt(m[2]!, 10);
    const cards = turns[ti]?.sseData?.result?.results;
    if (!cards || ci >= cards.length) return null;
    return cards[ci]!;
  }

  /**
   * Resolve `entry.sseData.pick_result.pickedIds` (array of `tXcY` refs)
   * to actual ResultCard objects from prior turns. Returns [] when this
   * isn't a pick turn or none of the IDs resolve. Done at the parent
   * level so each turn doesn't need access to the global turn history.
   */
  private resolvePickedCards(entry: TurnEntry, turns: TurnEntry[]): ResultCard[] {
    const ids = entry.sseData.pick_result?.pickedIds;
    if (!ids || ids.length === 0) return [];
    const out: ResultCard[] = [];
    for (const id of ids) {
      const m = id.match(/^t(\d+)c(\d+)$/);
      if (!m) continue;
      const ti = parseInt(m[1]!, 10);
      const ci = parseInt(m[2]!, 10);
      const t = turns[ti];
      const cards = t?.sseData?.result?.results;
      if (cards && ci < cards.length) {
        out.push(cards[ci]!);
      }
    }
    return out;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-message-list': EmmaChatMessageList;
  }
}
